import {
  KINETIC_MIN_START_SPEED,
  KINETIC_MAX_SPEED,
  KINETIC_STOP_SPEED,
  KINETIC_DAMPING_PER_MS,
  KINETIC_SAMPLE_WINDOW_MS,
} from "../constants";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * Inertia of the horizontal pan: measures the speed of the last moves of a
 * drag and, after release, keeps scrolling with a decaying speed. Frames
 * run only while it moves (scheduler.animate).
 *
 * @param {object} params
 * @param {(step: (timestamp: number) => boolean) => () => void} params.animate
 * @param {(pixels: number) => boolean} params.scrollBy - false when an edge stops the view
 */
export const createKineticScroll = ({ animate, scrollBy }) => {
  let samples = [];
  let stopAnimation = null;

  const stop = () => {
    stopAnimation?.();
    stopAnimation = null;
  };

  const releaseSpeed = (time) => {
    const recent = samples.filter((sample) => time - sample.time <= KINETIC_SAMPLE_WINDOW_MS);
    const first = recent[0];
    const last = recent[recent.length - 1];

    if (recent.length < 2 || last.time <= first.time) return 0;

    return (last.x - first.x) / (last.time - first.time);
  };

  return {
    stop,

    /** A drag started at `x` (CSS px) at `time` (event.timeStamp). */
    begin: (x, time) => {
      stop();
      samples = [{ x, time }];
    },

    track: (x, time) => {
      samples.push({ x, time });
      samples = samples.filter((sample) => time - sample.time <= KINETIC_SAMPLE_WINDOW_MS);
    },

    /** The drag ended at `time`: continues the movement when it was fast enough. */
    release: (time) => {
      const startSpeed = releaseSpeed(time);

      samples = [];

      if (Math.abs(startSpeed) < KINETIC_MIN_START_SPEED) return;

      let speed = clamp(startSpeed, -KINETIC_MAX_SPEED, KINETIC_MAX_SPEED);
      let previousTime = time;

      stopAnimation = animate((timestamp) => {
        const elapsed = Math.max(0, timestamp - previousTime);
        const hasMoved = scrollBy(speed * elapsed);

        previousTime = timestamp;
        speed *= KINETIC_DAMPING_PER_MS ** elapsed;

        const isMoving = (hasMoved || elapsed === 0) && Math.abs(speed) >= KINETIC_STOP_SPEED;

        if (!isMoving) stopAnimation = null;

        return isMoving;
      });
    },
  };
};
